import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Calendar as CalendarIcon, Clock, User, PawPrint, Scissors, MessageSquare, Send, X } from 'lucide-react';
import { format, addDays, isMonday, setHours, setMinutes } from 'date-fns';
import { pt } from 'date-fns/locale';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Calendar } from '@/components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from 'sonner';

const WHATSAPP_NUMBER = "351913918967";

const services = [
  { value: 'banho', label: 'Banho' },
  { value: 'tosquia', label: 'Tosquia' },
  { value: 'banho-tosquia', label: 'Banho + Tosquia' },
];

// Generate time slots from 10:00 to 17:30 (last slot at 17:30)
const generateTimeSlots = () => {
  const slots = [];
  for (let hour = 10; hour < 18; hour++) {
    slots.push(`${hour.toString().padStart(2, '0')}:00`);
    if (hour < 17 || (hour === 17 && true)) {
      slots.push(`${hour.toString().padStart(2, '0')}:30`);
    }
  }
  return slots.filter(slot => slot !== '17:30' || true);
};

const timeSlots = generateTimeSlots();

export const BookingForm = ({ isModal = false, onClose }) => {
  const [formData, setFormData] = useState({
    tutorName: '',
    petName: '',
    service: '',
    date: null,
    time: '',
    observations: '',
  });
  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showPreview, setShowPreview] = useState(false);

  // Load saved data from localStorage
  useEffect(() => {
    const savedData = localStorage.getItem('petGuardioesBooking');
    if (savedData) {
      const parsed = JSON.parse(savedData);
      setFormData({
        ...parsed,
        date: parsed.date ? new Date(parsed.date) : null,
      });
    }
  }, []);

  // Save to localStorage on change
  useEffect(() => {
    if (formData.tutorName || formData.petName || formData.service) {
      localStorage.setItem('petGuardioesBooking', JSON.stringify({
        ...formData,
        date: formData.date?.toISOString(),
      }));
    }
  }, [formData]);

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: null }));
    }
  };

  const validateForm = () => {
    const newErrors = {};
    if (!formData.tutorName.trim()) {
      newErrors.tutorName = 'Por favor, insira o seu nome';
    }
    if (!formData.service) {
      newErrors.service = 'Por favor, selecione um serviço';
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const generateWhatsAppMessage = () => {
    const serviceLabel = services.find(s => s.value === formData.service)?.label || formData.service;
    
    let message = `Olá Pet Guardiões 👋

Gostaria de reservar um serviço:

🐾 Serviço: ${serviceLabel}
👤 Tutor: ${formData.tutorName}`;

    if (formData.petName) {
      message += `\n🐶 Pet: ${formData.petName}`;
    }

    if (formData.observations) {
      message += `\n📝 Observações: ${formData.observations}`;
    }

    message += '\n\nObrigado(a)!';

    return message;
  };

  const handlePreview = () => {
    if (validateForm()) {
      setShowPreview(true);
    }
  };

  const handleSubmit = () => {
    const message = encodeURIComponent(generateWhatsAppMessage());
    const isMobile = /iPhone|iPad|iPod|Android/i.test(navigator.userAgent);
    const url = isMobile 
      ? `https://wa.me/${WHATSAPP_NUMBER}?text=${message}`
      : `https://web.whatsapp.com/send?phone=${WHATSAPP_NUMBER}&text=${message}`;
    
    // Save booking to localStorage
    const bookings = JSON.parse(localStorage.getItem('petGuardioesBookings') || '[]');
    bookings.push({
      ...formData,
      date: formData.date?.toISOString(),
      createdAt: new Date().toISOString(),
    });
    localStorage.setItem('petGuardioesBookings', JSON.stringify(bookings));

    toast.success('A abrir WhatsApp...', {
      description: 'Complete o envio da mensagem no WhatsApp',
    });

    window.open(url, '_blank');
    setShowPreview(false);
    
    if (onClose) {
      onClose();
    }
  };

  // Disable Mondays
  const disabledDays = (date) => {
    return isMonday(date) || date < new Date();
  };

  const formContent = (
    <div className="space-y-6">
      {/* Tutor Name */}
      <div className="space-y-2">
        <Label htmlFor="tutorName" className="flex items-center gap-2 text-gray-700">
          <User className="w-4 h-4" />
          Nome do tutor *
        </Label>
        <Input
          id="tutorName"
          placeholder="O seu nome"
          value={formData.tutorName}
          onChange={(e) => handleChange('tutorName', e.target.value)}
          className={`h-12 rounded-xl border-2 ${errors.tutorName ? 'border-red-400' : 'border-gray-200 focus:border-yellow-500'}`}
          data-testid="booking-tutor-name"
        />
        {errors.tutorName && (
          <p className="text-sm text-red-500">{errors.tutorName}</p>
        )}
      </div>

      {/* Pet Name */}
      <div className="space-y-2">
        <Label htmlFor="petName" className="flex items-center gap-2 text-gray-700">
          <PawPrint className="w-4 h-4" />
          Nome do animal (opcional)
        </Label>
        <Input
          id="petName"
          placeholder="Nome do seu patudo"
          value={formData.petName}
          onChange={(e) => handleChange('petName', e.target.value)}
          className="h-12 rounded-xl border-2 border-gray-200 focus:border-yellow-500"
          data-testid="booking-pet-name"
        />
      </div>

      {/* Service */}
      <div className="space-y-2">
        <Label className="flex items-center gap-2 text-gray-700">
          <Scissors className="w-4 h-4" />
          Tipo de serviço *
        </Label>
        <Select
          value={formData.service}
          onValueChange={(value) => handleChange('service', value)}
        >
          <SelectTrigger 
            className={`h-12 rounded-xl border-2 ${errors.service ? 'border-red-400' : 'border-gray-200 focus:border-yellow-500'}`}
            data-testid="booking-service-select"
          >
            <SelectValue placeholder="Selecione um serviço" />
          </SelectTrigger>
          <SelectContent>
            {services.map((service) => (
              <SelectItem key={service.value} value={service.value}>
                {service.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        {errors.service && (
          <p className="text-sm text-red-500">{errors.service}</p>
        )}
      </div>

      {/* Observations */}
      <div className="space-y-2">
        <Label htmlFor="observations" className="flex items-center gap-2 text-gray-700">
          <MessageSquare className="w-4 h-4" />
          Observações (opcional)
        </Label>
        <Textarea
          id="observations"
          placeholder="Digite a data e horário desejados. Confirmaremos se está disponível."
          value={formData.observations}
          onChange={(e) => handleChange('observations', e.target.value)}
          className="min-h-[100px] rounded-xl border-2 border-gray-200 focus:border-yellow-500 resize-none"
          data-testid="booking-observations"
        />
      </div>

      {/* Submit Button */}
      <Button
        onClick={handlePreview}
        className="w-full h-14 bg-yellow-500 hover:bg-yellow-400 text-gray-900 font-bold text-lg rounded-full shadow-lg shadow-yellow-500/30 hover:shadow-yellow-500/50 transition-all"
        data-testid="booking-preview-btn"
      >
        <Send className="w-5 h-5 mr-2" />
        Pré-visualizar e Enviar
      </Button>
    </div>
  );

  // Preview Modal
  const previewContent = (
    <AnimatePresence>
      {showPreview && (
        <motion.div
          className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          data-testid="booking-preview-modal"
        >
          <motion.div
            className="bg-white rounded-3xl p-6 max-w-md w-full max-h-[90vh] overflow-y-auto"
            initial={{ scale: 0.9, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            exit={{ scale: 0.9, opacity: 0 }}
          >
            <div className="flex justify-between items-center mb-4">
              <h3 className="font-nunito text-xl font-bold text-gray-900">
                Confirmar Reserva
              </h3>
              <button
                onClick={() => setShowPreview(false)}
                className="p-2 hover:bg-gray-100 rounded-full"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="bg-gray-50 rounded-2xl p-4 mb-6">
              <pre className="whitespace-pre-wrap text-sm text-gray-700 font-sans">
                {generateWhatsAppMessage()}
              </pre>
            </div>

            <div className="flex gap-3">
              <Button
                onClick={() => setShowPreview(false)}
                variant="outline"
                className="flex-1 h-12 rounded-full"
              >
                Editar
              </Button>
              <Button
                onClick={handleSubmit}
                className="flex-1 h-12 bg-green-500 hover:bg-green-400 text-white font-bold rounded-full"
                data-testid="booking-confirm-btn"
              >
                Enviar via WhatsApp
              </Button>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );

  if (isModal) {
    return (
      <>
        {formContent}
        {previewContent}
      </>
    );
  }

  return (
    <section 
      id="agendamento" 
      className="py-20 md:py-28 bg-white relative overflow-hidden"
      data-testid="booking-section"
    >
      {/* Background decoration */}
      <div className="absolute top-0 left-0 w-72 h-72 bg-yellow-200/30 rounded-full blur-3xl -z-10" />
      <div className="absolute bottom-0 right-0 w-96 h-96 bg-yellow-100/40 rounded-full blur-3xl -z-10" />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid lg:grid-cols-2 gap-12 lg:gap-20 items-start">
          {/* Left - Info */}
          <motion.div
            initial={{ opacity: 0, x: -30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5 }}
          >
            <span className="inline-flex items-center gap-2 text-yellow-600 font-semibold mb-4">
              <CalendarIcon className="w-4 h-4" />
              Agendamento fácil
            </span>
            <h2 className="font-nunito text-3xl sm:text-4xl lg:text-5xl font-extrabold text-gray-900 mb-6">
              Marque o banho ou tosquia<br />
              <span className="text-yellow-500">do seu patudo</span>
            </h2>
            <p className="text-lg text-gray-600 mb-8">
              Preencha o formulário e será direcionado para o WhatsApp com a mensagem já formatada. 
              Simples, rápido e direto!
            </p>

            <div className="space-y-4">
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 bg-yellow-100 rounded-2xl flex items-center justify-center flex-shrink-0">
                  <Clock className="w-6 h-6 text-yellow-600" />
                </div>
                <div>
                  <h4 className="font-bold text-gray-900">Horário de Funcionamento</h4>
                  <p className="text-gray-600">Terça a Sábado: 10h00 - 18h00</p>
                  <p className="text-gray-500 text-sm">Segunda-feira: encerrado</p>
                </div>
              </div>

              <div className="flex items-start gap-4">
                <div className="w-12 h-12 bg-yellow-100 rounded-2xl flex items-center justify-center flex-shrink-0">
                  <PawPrint className="w-6 h-6 text-yellow-600" />
                </div>
                <div>
                  <h4 className="font-bold text-gray-900">Atendimento Personalizado</h4>
                  <p className="text-gray-600">Cada animal recebe atenção individual e cuidado especial</p>
                </div>
              </div>
            </div>
          </motion.div>

          {/* Right - Form */}
          <motion.div
            className="bg-white rounded-3xl p-6 md:p-8 shadow-2xl shadow-yellow-500/10 border border-gray-100"
            initial={{ opacity: 0, x: 30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5 }}
          >
            <h3 className="font-nunito text-2xl font-bold text-gray-900 mb-6">
              Agendar Serviço
            </h3>
            {formContent}
          </motion.div>
        </div>
      </div>

      {previewContent}
    </section>
  );
};
